import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * Enhanced Shared Resize Manager
 * Single global resize listener with intelligent updates
 *
 * Features:
 * - Single window resize listener (instead of 16+)
 * - RAF throttling for smooth 60fps updates
 * - Debouncing for final resize event
 * - Breakpoint detection (mobile/tablet/desktop)
 * - Only notifies when values actually change
 * - Memory-efficient subscriber pattern
 */

// Breakpoints
const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
};

// Viewport types
const VIEWPORT = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
};

// Global state
let currentWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
let currentViewport = getViewportType(currentWidth);

// Subscriber management
const subscribers = new Set();
const breakpointSubscribers = new Map(); // Track which breakpoints each subscriber cares about

// RAF management
let rafId = null;
let debounceTimeout = null;

/**
 * Get viewport type from width
 */
function getViewportType(width) {
  if (width <= BREAKPOINTS.mobile) return VIEWPORT.MOBILE;
  if (width <= BREAKPOINTS.tablet) return VIEWPORT.TABLET;
  return VIEWPORT.DESKTOP;
}

/**
 * Notify subscribers of resize
 */
function notifySubscribers(width, viewport, isDebounced = false) {
  const resizeData = {
    width,
    viewport,
    isMobile: viewport === VIEWPORT.MOBILE,
    isTablet: viewport === VIEWPORT.TABLET,
    isDesktop: viewport === VIEWPORT.DESKTOP,
    isDebounced, // True when resize has finished
  };

  subscribers.forEach(callback => {
    try {
      callback(resizeData);
    } catch (error) {
      console.error('Error in resize subscriber:', error);
    }
  });
}

/**
 * Handle resize with RAF throttling
 */
function handleResize() {
  // Cancel any pending RAF
  if (rafId) {
    cancelAnimationFrame(rafId);
  }

  // Use RAF for smooth 60fps updates
  rafId = requestAnimationFrame(() => {
    const newWidth = window.innerWidth;
    const newViewport = getViewportType(newWidth);

    // Only notify if something actually changed
    const widthChanged = newWidth !== currentWidth;
    const viewportChanged = newViewport !== currentViewport;

    if (widthChanged || viewportChanged) {
      currentWidth = newWidth;
      currentViewport = newViewport;

      // Immediate notification (throttled by RAF)
      notifySubscribers(currentWidth, currentViewport, false);
    }

    // Clear any existing debounce timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Debounced notification (fires when resize finishes)
    debounceTimeout = setTimeout(() => {
      notifySubscribers(currentWidth, currentViewport, true);
    }, 150);
  });
}

// Attach single global listener (passive for better performance)
if (typeof window !== 'undefined') {
  window.addEventListener('resize', handleResize, { passive: true });

  // Also listen for orientation change on mobile
  window.addEventListener('orientationchange', handleResize, { passive: true });
}

/**
 * Hook: Subscribe to window resize events
 *
 * @param {Function} callback - Called with { width, viewport, isMobile, isTablet, isDesktop, isDebounced }
 * @param {Object} options - Configuration options
 * @param {boolean} options.debounceOnly - Only notify on debounced (final) resize
 * @returns {Object} Current viewport info
 */
export const useSharedResize = (callback, options = {}) => {
  const { debounceOnly = false } = options;
  const callbackRef = useRef(callback);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Wrapper that respects options
    const subscriber = (resizeData) => {
      // Skip immediate updates if debounceOnly is true
      if (debounceOnly && !resizeData.isDebounced) {
        return;
      }
      callbackRef.current(resizeData);
    };

    // Subscribe
    subscribers.add(subscriber);

    // Initial call with current values
    subscriber({
      width: currentWidth,
      viewport: currentViewport,
      isMobile: currentViewport === VIEWPORT.MOBILE,
      isTablet: currentViewport === VIEWPORT.TABLET,
      isDesktop: currentViewport === VIEWPORT.DESKTOP,
      isDebounced: true,
    });

    // Cleanup
    return () => {
      subscribers.delete(subscriber);
    };
  }, [debounceOnly]);
};

/**
 * Hook: Get current viewport info as state
 * Automatically updates when viewport changes
 *
 * @param {Object} options - Configuration options
 * @returns {Object} { width, viewport, isMobile, isTablet, isDesktop }
 */
export const useViewport = (options = {}) => {
  const [viewportInfo, setViewportInfo] = useState({
    width: currentWidth,
    viewport: currentViewport,
    isMobile: currentViewport === VIEWPORT.MOBILE,
    isTablet: currentViewport === VIEWPORT.TABLET,
    isDesktop: currentViewport === VIEWPORT.DESKTOP,
  });

  useSharedResize(
    useCallback((resizeData) => {
      setViewportInfo({
        width: resizeData.width,
        viewport: resizeData.viewport,
        isMobile: resizeData.isMobile,
        isTablet: resizeData.isTablet,
        isDesktop: resizeData.isDesktop,
      });
    }, []),
    options
  );

  return viewportInfo;
};

/**
 * Hook: Get responsive value based on breakpoint
 *
 * @param {Object} values - { mobile, tablet, desktop }
 * @returns {*} Value for current breakpoint
 *
 * @example
 * const columns = useResponsiveValue({ mobile: 1, tablet: 2, desktop: 4 });
 */
export const useResponsiveValue = (values) => {
  const { viewport } = useViewport({ debounceOnly: true });

  if (viewport === VIEWPORT.MOBILE && values.mobile !== undefined) {
    return values.mobile;
  }
  if (viewport === VIEWPORT.TABLET && values.tablet !== undefined) {
    return values.tablet;
  }
  return values.desktop;
};

/**
 * Get current viewport info (non-reactive, for one-time checks)
 */
export const getCurrentViewport = () => ({
  width: currentWidth,
  viewport: currentViewport,
  isMobile: currentViewport === VIEWPORT.MOBILE,
  isTablet: currentViewport === VIEWPORT.TABLET,
  isDesktop: currentViewport === VIEWPORT.DESKTOP,
});

/**
 * Get subscriber count (for debugging)
 */
export const getSubscriberCount = () => subscribers.size;

/**
 * Export viewport constants
 */
export { VIEWPORT, BREAKPOINTS };
